import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import api from "../../../../services/api";
import { useWeddingWizard } from "../../../../context/WeddingWizardContext";
import RentalCard from "./RentalCard";
import { toISO, round2, lineDeposit } from "./utils";
import { money } from "../../../../utils/money";

export default function RentalsStep() {
  const { state, upsertRental } = useWeddingWizard();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const todayISO = toISO(new Date());

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/rentals/products");
        if (!mounted) return;
        const items = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
          ? data
          : [];
        setProducts(items.filter((p) => p?.active !== false));
      } catch (e) {
        console.error("Load rentals failed", e);
        if (mounted) setProducts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Index current selections by productId:variantId
  const selectionByKey = useMemo(() => {
    const m = new Map();
    const items = Array.isArray(state?.rentals?.items)
      ? state.rentals.items
      : [];
    for (const r of items) {
      const key = `${r.productId}:${r.variantId}`;
      m.set(key, r);
    }
    return m;
  }, [state.rentals]);

  // Totals (rental + deposits)
  const totals = useMemo(() => {
    const items = Array.isArray(state?.rentals?.items)
      ? state.rentals.items
      : [];
    let rentalSubtotal = 0;
    let depositTotal = 0;

    for (const line of items) {
      const prod = products.find((p) => p.id === line.productId);
      if (!prod || !line.days || !line.quantity) continue;

      const variant = (Array.isArray(prod.variants) &&
        prod.variants.find((v) => v.id === line.variantId)) || {
        id: "default",
      };

      const dailyRate = Number(prod.dailyRate) || 0;
      rentalSubtotal += dailyRate * Number(line.days) * Number(line.quantity);
      depositTotal += lineDeposit(prod, variant, Number(line.quantity));
    }

    return {
      rentalSubtotal: round2(rentalSubtotal),
      depositTotal: round2(depositTotal),
      grand: round2(rentalSubtotal + depositTotal),
    };
  }, [state.rentals, products]);

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        Add rentals (optional)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Rent the full set where applicable. Deposits may apply.
      </Typography>

      <Grid container spacing={2}>
        {(loading ? Array.from({ length: 4 }) : products).map((p, i) => {
          const product = p || {};
          // Find an existing selection (any variant) for this product
          const existingKey = [...selectionByKey.keys()].find((k) =>
            k.startsWith(`${product.id}:`)
          );
          const sel = existingKey ? selectionByKey.get(existingKey) : null;

          const onUpsert = (patch) => {
            const hasVariants =
              Array.isArray(product.variants) && product.variants.length > 0;
            const variantId =
              patch.variantId ||
              sel?.variantId ||
              (hasVariants ? product.variants[0].id : "default");
            if (!product.id || !variantId) return;

            const chosen =
              (hasVariants &&
                product.variants.find((v) => v.id === variantId)) ||
              null;

            upsertRental({
              productId: product.id,
              variantId,
              startDate: patch.startDate ?? sel?.startDate ?? todayISO,
              endDate: patch.endDate ?? sel?.endDate ?? todayISO,
              days: patch.days ?? sel?.days ?? 1,
              quantity: Math.max(0, patch.quantity ?? sel?.quantity ?? 0),
              productTitle: product.title,
              variantLabel: hasVariants && chosen ? chosen.label : undefined,
              unitLabel: product.unitLabel || "set",
              attributes: chosen?.attributes || {},
            });
          };

          return (
            <Grid key={product.id || i} item xs={12} sm={6} md={4}>
              <RentalCard
                product={product}
                selection={sel}
                onUpsert={onUpsert}
              />
            </Grid>
          );
        })}
      </Grid>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 3,
          flexWrap: "wrap",
        }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Rentals subtotal: {money(totals.rentalSubtotal)}
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Deposits: {money(totals.depositTotal)}
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Total (rentals + deposits): {money(totals.grand)}
        </Typography>
      </Box>
    </Box>
  );
}
